// Кнопка «Добавить в…» — недраг-способ положить блок из библиотеки в вариант.
// Полностью управляется с клавиатуры: см. ТЗ по доступности в CONTRACT.md.
import { useEffect, useRef, useState } from 'react';
import { useActions, useStore } from '../../store';
import { useT } from '../../i18n';
import { AddToBoardIcon } from './ActionIcons';
import styles from './AddToVariantButton.module.css';

export interface AddToVariantButtonProps {
  segmentId: string;
  className?: string;
}

export function AddToVariantButton({ segmentId, className }: AddToVariantButtonProps) {
  const state = useStore();
  const actions = useActions();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const variants = state.variants;
  const hasVariants = variants.length > 0;

  useEffect(() => {
    if (!open) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function pick(variantId: string) {
    actions.addToVariant(variantId, segmentId);
    close();
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (!hasVariants) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveIndex(0);
      setOpen(true);
    }
  }

  function onMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % variants.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + variants.length) % variants.length);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(variants.length - 1);
      return;
    }
    if (e.key === 'Tab') {
      close();
    }
  }

  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <button
        ref={buttonRef}
        type="button"
        className={`btn btn-sm ${styles.trigger}`}
        aria-haspopup="menu"
        aria-expanded={open}
        // Кнопка стала иконкой, и подпись целиком ушла в метку: попап,
        // клавиатура и aria-haspopup/aria-expanded при этом не меняются —
        // менялось только содержимое кнопки. Подсказку при наведении соберёт
        // из этой же метки autoTitle (ui/shell/autoTitle.ts), а свой title
        // остаётся там, где он объясняет, почему жать некуда.
        aria-label={t('library.addTo')}
        disabled={!hasVariants}
        title={hasVariants ? undefined : t('library.addTo.none')}
        onClick={(e) => {
          e.stopPropagation();
          if (!hasVariants) return;
          setActiveIndex(0);
          setOpen((v) => !v);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <AddToBoardIcon />
      </button>
      {open && hasVariants && (
        <div
          ref={menuRef}
          role="menu"
          className={styles.menu}
          aria-label={t('library.addTo.pick')}
          onKeyDown={onMenuKeyDown}
        >
          {variants.map((v, i) => {
            const already = v.segmentIds.includes(segmentId);
            const locked = v.locked === true;
            return (
              <button
                key={v.id}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={locked}
                title={locked ? t('library.addTo.locked') : undefined}
                className={`${styles.item} ${i === activeIndex ? styles.active : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (locked) return;
                  pick(v.id);
                }}
              >
                <span className={styles.dot} style={{ background: v.color }} aria-hidden="true" />
                <span className={styles.name}>{v.name}</span>
                {locked && (
                  <span className={styles.check} aria-hidden="true">
                    🔒
                  </span>
                )}
                {!locked && already && (
                  <span className={styles.check} aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
